import React from 'react';
import { cn } from '@ideoz/ui';
import { Markdown } from './Markdown';
import type { UserMessageProps } from '../../types/UserMessage.types';

/**
 * UserMessage Component
 *
 * A simple display component for rendering user messages in chat interfaces.
 * Supports RTL/LTR text direction and custom styling.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <UserMessage>Hello, this is my message!</UserMessage>
 *
 * // With RTL support
 * <UserMessage dir="rtl">مرحبا، هذه رسالتي</UserMessage>
 *
 * // With custom styling
 * <UserMessage className="border-2 border-blue-500">Custom styled message</UserMessage>
 * ```
 */
const UserMessage: React.FC<UserMessageProps> = ({ children, className, dir, isPrototypeOpen }) => {
  return (
    <div
      dir={dir}
      data-testid="user-message"
      className={cn(
        'flex px-3 py-2 flex-col items-end gap-1.5 rounded-[24px] rounded-tr-[4px] bg-primary text-primary-foreground font-normal leading-[130%] lg:px-4 lg:py-3',
        isPrototypeOpen ? 'text-sm' : 'text-sm',
        className
      )}
    >
      {/* Render string content as markdown so the user's own formatting
          (bold, lists, …) shows — matching what the WYSIWYG composer sent. */}
      {typeof children === 'string' ? (
        <Markdown isRtl={dir === 'rtl'} className="">
          {children}
        </Markdown>
      ) : (
        children
      )}
    </div>
  );
};

export default UserMessage;
